import { orientationsFromConfig, orientationSummary, systemDesignForOrientation, updateOrientation } from "./orientations";
import { applyGroundPlanDesign } from "./groundPlanDesign";
import { physicalLayoutCount, physicalLayoutDesignKey, updateLayoutFromDesign } from "../../utils/physicalLayoutDesign";
import { roofInitialGrid } from "../../utils/roofInitialGrid";
import { prepareLayoutShading } from "../../utils/prepareLayoutShading";

// Deliberately restricted to a NEW single-surface plan. An existing layout,
// drawn boundary, imported mesh, or measured shading belongs to its author.
export function canSaveSimpleAutoLayout(config, generated, drafts = {}, sceneEdits = {}) {
  if (!generated || config?.pvDetailedShading?.enabled || config?.loc_info?.parcel?.length ||
      orientationsFromConfig(config).length !== 1 ||
      ![undefined, "", "fixed"].includes(config?.mountType) ||
      Object.keys(sceneEdits).length || Object.values(drafts).some(d => d?.dirty || d?.shadingDirty)) return false;
  const records = [config?.pv_design, ...(config?.orientations || [])];
  if (records.some(r => r?.parcel?.length || Object.keys(r?.spacingInfo || r?.spacing || {}).length ||
      r?.obstacleShading?.table || r?.obstacleShading?.placement)) return false;
  return !generated.site_geometry?.roof && !generated.site_geometry?.obstacles?.length &&
    !generated.parcel_outline && !generated.manual_layout_edited && !generated.module_array &&
    !generated.excluded_slots?.length && !Object.keys(generated.module_offsets || {}).length;
}

// Preserve the author's geometry, even when the proposed wiring changes.
// Obsolete string assignments are flagged, never silently repositioned.
export function preserveAutoDesignLayout(previous, config) {
  let next = config;
  const before = orientationsFromConfig(previous), after = orientationsFromConfig(config);
  let changed = previous.read_module !== config.read_module;
  for (const plane of before) {
    const old = plane.primary ? previous.pv_design : plane;
    const wiringChanged = previous.read_module !== config.read_module ||
      physicalLayoutDesignKey(systemDesignForOrientation(previous.systemDesign, before, plane.id)) !==
      physicalLayoutDesignKey(systemDesignForOrientation(config.systemDesign, after, plane.id));
    const patch = Object.fromEntries(["spacingInfo", "spacing", "obstacleShading", "sceneViews"].map(k => [k, old?.[k]]));
    const spacing = old?.spacingInfo || old?.spacing;
    if (wiringChanged && spacing) {
      patch.spacingInfo = { ...spacing, module_mapping_valid: false, scene_review_required: true };
      changed = true;
    }
    next = plane.primary ? { ...next, pv_design: { ...next.pv_design, ...patch } } : updateOrientation(next, plane.id, patch);
  }
  if (changed) {
    for (const plane of orientationsFromConfig(next)) {
      const record = plane.primary ? next.pv_design : plane;
      const patch = { sceneViews: [], obstacleShading: { ...record?.obstacleShading, stale: true } };
      next = plane.primary ? { ...next, pv_design: { ...next.pv_design, ...patch } } : updateOrientation(next, plane.id, patch);
    }
    if (next.pvDetailedShading?.scene) next = { ...next, pvDetailedShading: { ...next.pvDetailedShading, scene: undefined } };
  }
  return next;
}

export async function saveSimpleAutoLayout(previous, config, generated, { module, fetchMapping, greek, ...options }) {
  const chars = module?.characteristics || module || {};
  const length = Number(chars.Length ?? chars.length), width = Number(chars.Width ?? chars.width);
  if (!(length > 0 && width > 0)) throw new Error(greek ? "Λείπουν οι διαστάσεις του πλαισίου για τη διάταξη." : "Module dimensions are required for the layout.");
  const po = config.panelOrientation || {};
  const count = orientationSummary(config)[0]?.modules;
  let spacing = roofInitialGrid({ ...generated, mode: "manual",
    minimum_distance_m: generated.minimum_distance_m ?? generated.pitch_m ?? generated.minimum_distance ?? generated.pitch,
    row_vertical_modules: generated.row_vertical_modules ?? 1,
  }, { installationType: po.installationType, moduleLength: length, moduleWidth: width,
    portrait: po.moduleOrientation !== "landscape", tilt: orientationsFromConfig(config)[0].tilt });
  const pitch = Number(spacing.minimum_distance_m);
  if (!(Number.isFinite(pitch) && pitch > 0 && count > 0)) throw new Error(greek ? "Ελέγξτε το βήμα και το πλήθος πλαισίων της διάταξης." : "Check layout pitch and module count.");
  if (physicalLayoutCount(spacing) !== count) spacing = updateLayoutFromDesign(spacing, {
    totalModules: count, moduleLength: length, moduleWidth: width, portrait: po.moduleOrientation !== "landscape",
  });
  spacing = { ...spacing, mode: "manual", source: "autodesign", pitch_m: Number(spacing.minimum_distance_m),
    minimum_distance_m: Number(spacing.minimum_distance_m), row_vertical_modules: Number(spacing.row_vertical_modules),
    field_rows: spacing.row_modules.length, string_routing: "continuous" };
  const id = orientationsFromConfig(config)[0].id;
  const mapped = await applyGroundPlanDesign(config, { id, spacing }, fetchMapping, greek);
  return prepareLayoutShading(previous, mapped.config, { ...options, orientationId: id, calculate: true, getModule: async () => module });
}
